import React, { useEffect } from 'react'
import { useHistory, Redirect } from 'react-router-dom'

// Constants
import { routes } from '@sketch/constants'

// Utils
import { useQueryParams } from 'utils'

// Hooks
import { useToast } from '@sketch/toasts'
import { useSetUserAuthorization } from 'modules/user/operations'
import { useModalContext } from 'containers/ModalManager'

// Components
import IndexLayout, { Title, Subtitle } from 'components/IndexLayout'
import SignInForm from 'components/SignInForm'
import Confirm2FADisabledModal from 'modules/user/modals/Confirm2FADisabledModal'

// Styles
import { Wrapper, StyledLink } from './DisableMFAView.styles'

// Types
import {
  useSignInAndDisableMfaMutation,
  GetInitialUserDocument,
} from '@sketch/gql-types'

/**
 * Part of the Disable 2Fa Flow, this component
 * renders a confirmation page to disable 2FA
 */
const DisableMFAView: React.FC = () => {
  const history = useHistory()
  const { showToast } = useToast()
  const { showModal } = useModalContext()
  const { token } = useQueryParams<'PERSONAL_SETTINGS_DISABLE_MFA'>()

  const setUserAuthorization = useSetUserAuthorization()

  const [signInAndDisableMfa, { loading }] = useSignInAndDisableMfaMutation({
    refetchQueries: () => [
      {
        query: GetInitialUserDocument,
      },
    ],
    awaitRefetchQueries: true,
    update: (_, { data }) => {
      if (!data) {
        return
      }

      setUserAuthorization(data.signInAndDisableMfa.credentials)
    },
    onCompleted: () => {
      history.push(routes.PERSONAL_SETTINGS.create({}))
      showModal(Confirm2FADisabledModal)
    },
    onError: 'show-toast',
  })

  /**
   * This page requires a token to be present in the URL
   */
  useEffect(() => {
    if (!token) {
      showToast('Something went wrong', 'negative')
    }
  }, [token, showToast])

  if (!token) {
    return <Redirect to={routes.ENTRY.create({})} push={false} />
  }

  return (
    <IndexLayout variant="teal">
      <Wrapper>
        <Title>Sign in and Disable 2FA</Title>
        <Subtitle>
          Enter your password to disable two-factor <br /> authentication (2FA).
        </Subtitle>
        <SignInForm
          submitCopy="Sign In and Disable 2FA"
          isLoading={loading}
          onSubmit={values => {
            signInAndDisableMfa({
              variables: {
                email: values.email,
                password: values.password,
                disableMfaToken: token!,
              },
            })
          }}
        />
        <StyledLink
          variant="secondary"
          to={{
            pathname: routes.ENTRY.create({}),
          }}
          isUnderlined
        >
          Cancel
        </StyledLink>
      </Wrapper>
    </IndexLayout>
  )
}

export default DisableMFAView
